/**
 * Recurring-invoice billing cadence — the single source of truth for the
 * "every month / quarter / half-year / year / every X months" presets shared by
 * both recurring-setup surfaces (the inline `SchedulingCard` and the standalone
 * `CreateRecurringScheduleModal`).
 *
 * The backend expresses cadence as `frequency` + `interval` ("every N periods").
 * These presets are just convenient (frequency, interval) pairs; the flexible
 * frequency dropdown + interval field remain available alongside them, so no
 * cadence that was expressible before is removed — the chips only add
 * one-click Monthly / Quarterly / Half-yearly / Yearly, and half-yearly /
 * every-X-months (which had no affordance) are now reachable.
 */
export type RecurringFrequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface CadencePreset {
  id: 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
  label: string;
  frequency: RecurringFrequency;
  interval: number;
}

// Half-yearly has no dedicated backend frequency — it is monthly × 6, which
// bills the chosen day-of-month every six months (more precise than a bare
// 6-month offset). Quarterly + Yearly keep their native frequencies.
export const CADENCE_PRESETS: CadencePreset[] = [
  { id: 'monthly', label: 'Monthly', frequency: 'monthly', interval: 1 },
  { id: 'quarterly', label: 'Quarterly', frequency: 'quarterly', interval: 1 },
  { id: 'half_yearly', label: 'Half-yearly', frequency: 'monthly', interval: 6 },
  { id: 'yearly', label: 'Yearly', frequency: 'yearly', interval: 1 },
];

/** The preset id matching a (frequency, interval) pair, or null for a custom mix. */
export function matchCadencePreset(
  frequency: string,
  interval: number,
): CadencePreset['id'] | null {
  const match = CADENCE_PRESETS.find(
    (p) => p.frequency === frequency && p.interval === (interval || 1),
  );
  return match ? match.id : null;
}

/** Human summary, e.g. "every month", "every 6 months (half-yearly)", "every 2 weeks". */
export function cadenceSummary(frequency: RecurringFrequency, interval: number): string {
  const n = Math.max(1, interval || 1);
  if (frequency === 'monthly' && n === 6) return 'every 6 months (half-yearly)';
  const unit =
    frequency === 'weekly'
      ? 'week'
      : frequency === 'monthly'
        ? 'month'
        : frequency === 'quarterly'
          ? 'quarter'
          : 'year';
  return n === 1 ? `every ${unit}` : `every ${n} ${unit}s`;
}

/** The label shown next to the interval field ("month(s)", "week(s)", …). */
export function cadenceUnitLabel(frequency: RecurringFrequency): string {
  switch (frequency) {
    case 'weekly':
      return 'week(s)';
    case 'monthly':
      return 'month(s)';
    case 'quarterly':
      return 'quarter(s)';
    default:
      return 'year(s)';
  }
}
